import Controller from '@ember/controller'
import { service } from '@ember/service'
import { action } from '@ember/object'
import { isBlank, isPresent } from '@ember/utils'
import type Store from '@ember-data/store'
import { tracked } from '@glimmer/tracking'
import { task } from 'ember-concurrency'
import { trackedFunction } from 'reactiveweb/function'
import { keepLatest } from 'reactiveweb/keep-latest'
import { buildDataOperationsConfigProxy } from 'teacher-ui-client/components/data-operations/data-operations-proxy'
import { TABLE_THEMES } from 'teacher-ui-client/manage/table-themes'
import { subscriptionTypeToThemeKey } from 'teacher-ui-client/utils/themes'
import { SortType, SortDirection } from 'teacher-ui-client/components/data-operations/table/sort-definition'
import { printImagesFitToPage } from 'teacher-ui-client/utils/print'
import type { IntlService } from 'ember-intl'
import type SessionService from 'teacher-ui-client/services/session'
import type StudentEventsService from 'teacher-ui-client/services/student-events'
import type DateFilterService from 'teacher-ui-client/services/date-filter'
import type CertificatesRoute from 'teacher-ui-client/manage/certificates/route'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { hasId } from 'teacher-ui-client/utils/type-guards'
import type { SchoolScope, StudentsScope } from 'teacher-ui-client/reporting/ui-scope/utils'
import type { CertificateEventsData } from 'teacher-ui-client/utils/student-events/fetchers/certificate-events'
import type { SelectOption } from 'teacher-ui-client/components/form-components/form-select/option'
import type Teacher from 'teacher-ui-client/models/teacher'
import { use } from 'ember-resources'
import type FeaturesService from 'teacher-ui-client/services/features'
import { capitalize } from '@ember/string'
import { buildLegacyCertificateUrl } from 'teacher-ui-client/utils/build-legacy-certificate-url'

export interface CertificateRow extends CertificateEventsData {
  studentName: string
  teacherIds: string[]
  teacherNames: string
}

export interface Column {
  label?: string
  valuePath?: string
  sortable?: SortType
  formatAsDate?: boolean
  selectable?: boolean
  isDownloadButton?: boolean
}

type CertificateProduct = LegacyProductRe | LegacyProductRex | LegacyProductFp | LegacyProductMs | LegacyProductWl

interface ProductOption extends SelectOption {
  text: string
  uid: CertificateProduct
  value: CertificateProduct
}

interface TypeOption extends SelectOption {
  value: string
}

export default class CertificatesController extends Controller<ModelFor<CertificatesRoute>> {
  @service intl!: IntlService

  @service session!: SessionService

  @service studentEvents!: StudentEventsService

  @service store!: Store

  @service features!: FeaturesService

  @service dateFilter!: DateFilterService

  get isSubco() {
    return this.session.currentTeacher.subcoFor(this.subscriptionType)
  }

  queryParams = [
    { sortCol: 'col' },
    { sortDir: 'dir' },
    { sortCompare: 'compare' },
    { currentPage: 'pageNumber' },
    { textFilter: 'filter' },
    { recordsPerPage: 'perPage' },
    'selectedIds',
    'product',
    'teacherId',
    'dateRange',
    'typeFilter',
  ] as const

  @tracked
  sortCol = 'createdAt'

  @tracked
  sortDir = SortDirection.Asc

  @tracked
  sortCompare = SortType.ByDate

  @tracked
  currentPage = 1

  @tracked
  recordsPerPage = 10

  @tracked
  textFilter = ''

  @tracked
  selectedIds: string[] = []

  @tracked product: CertificateProduct | '' = ''

  @tracked
  teacherId = ''

  @tracked
  dateRange = ''

  @tracked
  typeFilter = ''

  get certificateProduct(): CertificateProduct {
    if (this.product) return this.product

    if (this.subscriptionType === SubscriptionType.Reading) {
      return 're'
    } else if (
      this.subscriptionType === SubscriptionType.Writing &&
      this.features.isEnabled('tui_writing_certificates')
    ) {
      return 'writing_legends'
    } else {
      return 'maths'
    }
  }

  fetchCertificates = trackedFunction(this, async () => {
    if (!this.certificateProduct) return null

    const period = this.dateFilter.period

    const studentsForTeacher = this.session.currentTeacher
      .studentsForSubscriptionType(this.subscriptionType)
      .filter(hasId)
      .map((s) => s.id)

    const scope: SchoolScope | StudentsScope = this.isSubco
      ? { scope: 'school' as const, ids: [this.session.currentSchool.id] }
      : { scope: 'students' as const, ids: studentsForTeacher }

    return await this.studentEvents.certificateEvents(this.certificateProduct, scope, period)
  })

  @use
  latestCertificates = keepLatest({
    value: () => this.fetchCertificates.value,
    when: () => this.fetchCertificates.isPending,
  })

  activeTeachers = trackedFunction(this, async () => {
    const { subscriptionType } = this
    await Promise.resolve() // disconnect tracking before store calls
    return await this.store.query<Teacher>('teacher', { filter: { 'subscription-type': subscriptionType } })
  })

  get dataOperationsConfig() {
    return buildDataOperationsConfigProxy(this)
  }

  get certificateRows(): CertificateRow[] {
    return (
      this.latestCertificates
        ?.map((c) => {
          const student = this.model.students.find((s) => s.id === c.student_id.toString())
          if (!student) return null // may not be found if student deleted, no longer in subtype etc

          const teachersForStudent = student.teachersFor(this.subscriptionType).filter(hasId)

          const url =
            c.product === 'fast_phonics' ||
            c.product === 'writing_legends' ||
            c.product === 'maths' ||
            c.product === 'rex'
              ? buildLegacyCertificateUrl(c.product, c.precinct, c, student)
              : c.url

          return {
            ...c,
            url,
            studentName: student.fullName,
            teacherIds: teachersForStudent.map((t) => t.id),
            teacherNames: teachersForStudent.map((t) => t.fullName).join(', '),
          }
        })
        .filter(isPresent) ?? []
    )
  }

  get subscriptionType() {
    return this.model.subscriptionType
  }

  get tableTheme() {
    return TABLE_THEMES[subscriptionTypeToThemeKey(this.subscriptionType)]
  }

  get filteredByTeacher() {
    if (isBlank(this.teacherId)) {
      return this.certificateRows
    }
    return this.certificateRows.filter(
      (certificate) =>
        certificate.teacherIds?.some((id) => id === this.teacherId) ||
        (certificate.id && this.selectedIds.includes(certificate.id)),
    )
  }

  get filteredByType() {
    if (isBlank(this.typeFilter)) {
      return this.filteredByTeacher
    }
    return this.filteredByTeacher.filter((certificate) => {
      if (this.typeFilter === 'mental_minute') {
        return certificate.precinct === 'mental_minute' || (certificate.id && this.selectedIds.includes(certificate.id))
      }
      return (
        certificate.legacy_cert_details.color?.toLowerCase() === this.typeFilter.toLowerCase() ||
        (certificate.id && this.selectedIds.includes(certificate.id))
      )
    })
  }

  get filteredByText() {
    const { textFilter } = this
    if (isBlank(textFilter)) return this.filteredByType

    const filteredByText = this.filteredByType.filter((certificate) => {
      const content = [
        certificate.studentName,
        certificate.description,
        certificate.created_at,
        certificate.teacherNames,
      ].join(' ')
      return (
        content.toLowerCase().includes(textFilter.toLowerCase()) ||
        (certificate.id && this.selectedIds.includes(certificate.id))
      )
    })

    return filteredByText
  }

  get data() {
    return this.filteredByText.filter(hasId)
  }

  /**
   * Print certificates table column definition.
   */
  get columns(): Column[] {
    const { intl } = this
    const teachersColumns = [
      {
        label: intl.t('manage.certificates.teachers'),
        valuePath: 'teacherNames',
        sortable: SortType.ByValue,
      },
    ]

    return [
      {
        label: 'All',
        selectable: true,
      },
      {
        label: intl.t('manage.certificates.student'),
        valuePath: 'studentName',
        sortable: SortType.ByValue,
      },
      {
        label: intl.t('manage.certificates.certificate'),
        valuePath: 'description',
        sortable: SortType.ByValue,
      },
      ...(this.isSubco ? teachersColumns : []),
      {
        label: intl.t('manage.certificates.issueDate'),
        valuePath: 'created_at',
        sortable: SortType.ByDate,
        formatAsDate: true,
      },
      {
        isDownloadButton: true,
      },
    ]
  }

  get productOptions(): ProductOption[] {
    switch (this.subscriptionType) {
      case SubscriptionType.Reading:
        return [
          { text: this.intl.t('products.re'), uid: 're', value: 're' },
          { text: this.intl.t('products.rex'), uid: 'rex', value: 'rex' },
          { text: this.intl.t('products.fast_phonics'), uid: 'fast_phonics', value: 'fast_phonics' },
        ]
      case SubscriptionType.Maths:
        return [{ text: this.intl.t('products.ms'), uid: 'maths', value: 'maths' }]
      case SubscriptionType.Writing:
        // Only show Writing product option if the feature flag is enabled
        if (this.features.isEnabled('tui_writing_certificates')) {
          return [{ text: this.intl.t('products.writing-legends'), uid: 'writing_legends', value: 'writing_legends' }]
        }
        return []
      default:
        return []
    }
  }

  get teacherOptions() {
    const teachers: Teacher[] = this.activeTeachers.value ?? []
    return teachers.filter(hasId).map((teacher) => ({
      uid: teacher.id,
      value: teacher.id,
      text: teacher.fullName,
    }))
  }

  get typeOptions() {
    const colors = this.certificateRows
      .map((cert) => cert.legacy_cert_details.color)
      .filter(isPresent)
      .filter((color) => color.trim() !== '')

    const uniqueColors = [...new Set(colors)]

    // Want to have Gold, Silver, Bronze at the top in that order (if all present)
    const priorityOrder = ['gold', 'silver', 'bronze']

    const priorityColors: string[] = []
    const otherColors: string[] = []

    uniqueColors.forEach((color) => {
      const lowerColor = color.toLowerCase()
      if (priorityOrder.includes(lowerColor)) {
        // Insert in priority order
        const priorityIndex = priorityOrder.indexOf(lowerColor)
        priorityColors[priorityIndex] = color
      } else {
        otherColors.push(color)
      }
    })

    // Filter out undefined values and combine with sorted others
    const sortedColors = [...priorityColors.filter(isPresent), ...otherColors.sort()]

    const colorOptions = sortedColors.map((color) => ({
      uid: color,
      value: color,
      text: capitalize(color),
    }))

    if (this.subscriptionType !== SubscriptionType.Maths) {
      return colorOptions
    }

    // Check if any certificate has precinct = "mental_minute"
    const hasMentalMinute = this.certificateRows.some((cert) => cert.precinct === 'mental_minute')

    if (hasMentalMinute) {
      colorOptions.push({
        uid: 'mental_minute',
        value: 'mental_minute',
        text: 'Mental Minute',
      })
    }

    return colorOptions
  }

  printCertificates = task({ drop: true }, async (certificates: CertificateRow[]) => {
    const certificatesToPrint = certificates.map((certificate) => certificate.url)
    await printImagesFitToPage(certificatesToPrint.filter(isPresent))
  })

  @action
  setTextFilter(textFilter: string) {
    this.textFilter = textFilter
    this.currentPage = 1
  }

  @action
  setProduct(productOption: ProductOption) {
    this.product = productOption.value
    this.selectedIds = []
    this.currentPage = 1
  }

  @action
  setTeacherId(teacherId: string) {
    this.teacherId = teacherId
    this.currentPage = 1
  }

  @action
  setTypeFilter(typeOption: TypeOption) {
    this.typeFilter = typeOption.value
    this.currentPage = 1
  }
}
